import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';

import { deltaTone, formatDelta, formatNumber, toNumber } from '@/features/reports/lib/format';

const TONE_ICON = {
    up: ArrowUpRight,
    down: ArrowDownRight,
    flat: Minus,
};

/**
 * A KPI with its period-over-period change.
 *
 * `delta` is the backend's `ValueDelta` / `CountDelta` (`{ current, previous,
 * change_pct }`). `change_pct` is null when the previous window was empty, and
 * that renders as a neutral "—" rather than a misleading 0%.
 *
 * Set `invert` for metrics where a rise is bad (failure rate, days of cover
 * risk) so the colour reads correctly.
 */
export default function KpiTile({
    title,
    delta,
    format = formatNumber,
    icon: Icon,
    accent = 'green',
    invert = false,
    hint,
    secondary,
}) {
    const changePct = delta?.change_pct;
    const tone = deltaTone(changePct);
    const badgeTone = invert
        ? { up: 'down', down: 'up', flat: 'flat' }[tone]
        : tone;
    const Icon2 = TONE_ICON[tone];
    const label = formatDelta(changePct);

    const value = delta === undefined || delta === null ? '—' : format(delta.current ?? 0);

    return (
        <div className={`card report-kpi report-kpi-${accent}`}>
            <div className="report-kpi-top">
                <p className="report-kpi-title">{title}</p>
                {Icon && (
                    <span className={`report-kpi-icon report-kpi-icon-${accent}`}>
                        <Icon size={18} />
                    </span>
                )}
            </div>

            <p className="report-kpi-value">{value}</p>

            <div className="report-kpi-foot">
                {label === null ? (
                    <span className="report-delta report-delta-flat">
                        <Minus size={13} />
                        <span>بدون دوره قبل</span>
                    </span>
                ) : (
                    <span className={`report-delta report-delta-${badgeTone}`}>
                        <Icon2 size={13} />
                        <span>{label}</span>
                    </span>
                )}
                {secondary && <span className="report-kpi-secondary">{secondary}</span>}
            </div>

            {hint && <p className="report-kpi-hint">{hint}</p>}
        </div>
    );
}

/** A KPI for a plain scalar (no previous-period comparison available). */
export function SimpleKpi({ title, value, format = formatNumber, icon: Icon, accent = 'green', hint, secondary }) {
    return (
        <div className={`card report-kpi report-kpi-${accent}`}>
            <div className="report-kpi-top">
                <p className="report-kpi-title">{title}</p>
                {Icon && (
                    <span className={`report-kpi-icon report-kpi-icon-${accent}`}>
                        <Icon size={18} />
                    </span>
                )}
            </div>

            <p className="report-kpi-value">{format(toNumber(value))}</p>

            <div className="report-kpi-foot">
                {secondary && <span className="report-kpi-secondary">{secondary}</span>}
            </div>

            {hint && <p className="report-kpi-hint">{hint}</p>}
        </div>
    );
}
